/**
 * Pure rules of the one milestone form (P-05/C, D-52): the values, the
 * validation and the request body. Pinned by
 * `src/utils/__tests__/milestoneFormModel.test.ts`.
 *
 * Canonical direction: project → milestone → task. A milestone's parent is its
 * project (or, for a sub-milestone, a top-level milestone of the SAME project
 * — `parent_milestone_id`); a task points UP to its milestone through
 * `tickets.milestone_id`. No body ever carries `ticket_id` (the legacy
 * reversed link, milestones.ticket_id).
 */
import type { Translate } from './taskFormModel';
import type { MilestoneStatus } from '../../types';

export type PlannedHoursMode = 'undefined' | 'automatic' | 'manual';
export const MILESTONE_PLANNED_MODES: readonly PlannedHoursMode[] = ['undefined', 'automatic', 'manual'];

export interface MilestoneFormValues {
  /** Only when opened from /milestones: customer → project are picked first. */
  customerId: string;
  projectId: string;
  name: string;
  description: string;
  /** "אחראי" — optional. */
  responsibleId: string;
  /** "מצוותים" (milestone_assignees). */
  assigneeIds: string[];
  startDate: string;
  dueDate: string;
  status: MilestoneStatus;
  plannedMode: PlannedHoursMode;
  plannedManual: string;
  /** "תחת אבן דרך" — '' = a top-level milestone ("— ראשית —"). */
  parentId: string;
}

export const emptyMilestoneValues = (): MilestoneFormValues => ({
  customerId: '', projectId: '', name: '', description: '', responsibleId: '', assigneeIds: [],
  startDate: '', dueDate: '', status: 'planning', plannedMode: 'undefined', plannedManual: '', parentId: '',
});

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/** The form's values for an existing milestone (edit). */
export function milestoneValuesOf(m: {
  name?: string | null; description?: string | null; responsible_user_id?: string | null;
  assignees?: { user_id: string }[] | null; milestone_assignees?: { user_id: string }[] | null;
  start_date?: string | null; due_date?: string | null; status?: string | null;
  planned_hours_mode?: string | null; planned_hours_manual?: number | string | null; parent_milestone_id?: string | null;
  project_id?: string | null;
}): MilestoneFormValues {
  const mode = (MILESTONE_PLANNED_MODES as readonly string[]).includes(str(m.planned_hours_mode)) ? (m.planned_hours_mode as PlannedHoursMode) : 'undefined';
  return {
    ...emptyMilestoneValues(),
    projectId: str(m.project_id),
    name: str(m.name),
    description: str(m.description),
    responsibleId: str(m.responsible_user_id),
    assigneeIds: (m.assignees ?? m.milestone_assignees ?? []).map((a) => a.user_id),
    startDate: str(m.start_date).slice(0, 10),
    dueDate: str(m.due_date).slice(0, 10),
    status: (['planning', 'in_progress', 'in_review', 'testing', 'completed'].includes(str(m.status)) ? m.status : 'planning') as MilestoneStatus,
    plannedMode: mode,
    plannedManual: mode === 'manual' ? str(m.planned_hours_manual) : '',
    parentId: str(m.parent_milestone_id),
  };
}

/**
 * The request body (POST /projects/:id/milestones, POST /milestones with
 * `project_id` when the project was picked, PATCH /milestones/:id). An empty
 * value clears the field (null); the manual hours only with the manual mode.
 */
export function buildMilestoneBody(v: MilestoneFormValues, opts: { withProjectId?: boolean } = {}) {
  const manual = v.plannedManual.trim() === '' ? null : Number(v.plannedManual.replace(',', '.'));
  return {
    ...(opts.withProjectId ? { project_id: v.projectId } : {}),
    name: v.name.trim(),
    description: v.description.trim() || null,
    responsible_user_id: v.responsibleId || null,
    assignee_ids: [...v.assigneeIds],
    start_date: v.startDate || null,
    due_date: v.dueDate || null,
    status: v.status,
    planned_hours_mode: v.plannedMode,
    planned_hours_manual: v.plannedMode === 'manual' ? manual : null,
    parent_milestone_id: v.parentId || null,
  };
}

export const MILESTONE_FIELD_ORDER = ['ms-customer', 'ms-project', 'ms-name', 'ms-description', 'ms-responsible', 'ms-start', 'ms-due', 'ms-planned', 'ms-parent'] as const;

/** Field errors, keyed by control id (validated on blur and on submit). */
export function validateMilestoneForm(v: MilestoneFormValues, opts: { pickProject?: boolean }, t: Translate): Record<string, string | undefined> {
  const errors: Record<string, string | undefined> = {};
  if (opts.pickProject && !v.customerId) errors['ms-customer'] = t('milestoneForm.errors.customerRequired');
  if (opts.pickProject && !v.projectId) errors['ms-project'] = t('milestoneForm.errors.projectRequired');
  const name = v.name.trim();
  if (!name) errors['ms-name'] = t('milestoneForm.errors.nameRequired');
  else if (name.length < 2) errors['ms-name'] = t('milestoneForm.errors.nameTooShort');
  if (v.startDate && v.dueDate && v.startDate > v.dueDate) errors['ms-due'] = t('milestoneForm.errors.dateOrder');
  if (v.plannedMode === 'manual') {
    const s = v.plannedManual.trim();
    const n = Number(s.replace(',', '.'));
    if (!s || !Number.isFinite(n) || n < 0) errors['ms-planned'] = t('milestoneForm.errors.hoursInvalid');
  }
  return errors;
}

/**
 * "תחת אבן דרך" options: the TOP-LEVEL milestones of the same project (any —
 * a core one included, D-27), not archived, never the milestone itself.
 */
export function parentOptions<M extends { id: string; parent_milestone_id?: string | null; archived_at?: string | null }>(
  milestones: M[],
  selfId?: string,
): M[] {
  return milestones.filter((m) => !m.parent_milestone_id && !m.archived_at && m.id !== selfId);
}

/**
 * The parent field is locked when the milestone has sub-milestones (one
 * level, D-03) or is a core milestone (a sub-milestone is never core, D-55).
 */
export function parentLocked(selfId: string | undefined, isCore: boolean, milestones: { parent_milestone_id?: string | null }[]): boolean {
  if (!selfId) return false;
  return isCore || milestones.some((m) => m.parent_milestone_id === selfId);
}
